import { useEffect, useState } from "react";
import { Users, GraduationCap, Clock, UserCheck, UserX } from "lucide-react";
import api from "../lib/api";

// Compact tile that shows how many team members / participants are
// clocked in today + their combined hours. Used on the My Day rail so
// Founder / BDOM / PCWM open the app and immediately see the shift.
const TileHeader = ({ Icon, label, count, total }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2">
      <Icon size={16} className="text-brand"/>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)]">{label}</div>
    </div>
    <div className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)]">
      {count}<span className="text-[color:var(--aii-text-2)]">/{total}</span> present
    </div>
  </div>
);

const HoursChip = ({ hours }) => (
  <span className="text-[10px] font-mono tabular-nums px-1.5 py-0.5 rounded bg-[color:var(--aii-green-tint)] text-brand font-bold flex items-center gap-1 shrink-0">
    <Clock size={9}/>{hours ? hours.toFixed(1) : "0.0"}h
  </span>
);

const roleTone = {
  FOUNDER: "bg-brand text-white",
  BDOM: "bg-blue-600 text-white",
  GEHM: "bg-amber-500 text-white",
  PCWM: "bg-purple-600 text-white",
  SMCM: "bg-slate-600 text-white",
};

export default function TodayAttendanceSnapshotTiles({ user }) {
  const role = user?.real_role || user?.effective_role;
  const canTeam = role === "FOUNDER" || role === "BDOM";
  const canParticipant = role === "FOUNDER" || role === "BDOM" || role === "PCWM";
  const [team, setTeam] = useState(null);
  const [pax, setPax] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (canTeam) {
      api.get("/attendance/today-snapshot")
        .then(({ data }) => { if (!cancelled) setTeam(data); })
        .catch(() => { if (!cancelled) setTeam({ present: [], on_leave: [], unmarked: [], present_count: 0, total_team: 0, total_hours: 0 }); });
    }
    if (canParticipant) {
      api.get("/participant-attendance/today-snapshot")
        .then(({ data }) => { if (!cancelled) setPax(data); })
        .catch(() => { if (!cancelled) setPax({ present: [], on_leave: [], present_count: 0, total_hours: 0 }); });
    }
    return () => { cancelled = true; };
  }, [canTeam, canParticipant]);

  if (!canTeam && !canParticipant) return null;

  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="my-day-attendance-snapshots">
      {canTeam && (
        <div className="aii-card p-4" data-testid="myday-team-attendance">
          <TileHeader Icon={Users} label="Present employees · today" count={team?.present_count ?? "—"} total={team?.total_team ?? "—"}/>
          <div className="text-[10px] text-[color:var(--aii-muted)] uppercase tracking-widest flex items-center gap-3 mb-2">
            <span className="flex items-center gap-1"><Clock size={10}/> <b className="text-brand text-xs normal-case tracking-normal">{team?.total_hours ?? 0}h</b> logged</span>
            {team?.leave_count > 0 && <span className="flex items-center gap-1"><UserX size={10}/> {team.leave_count} leave</span>}
            {team?.unmarked_count > 0 && <span className="text-amber-700">· {team.unmarked_count} unmarked</span>}
          </div>
          {team === null && (
            <div className="text-xs text-[color:var(--aii-muted)] py-4 text-center">Loading…</div>
          )}
          {team && team.present.length === 0 && team.on_leave.length === 0 && (
            <div className="text-xs text-[color:var(--aii-muted)] py-4 text-center italic">
              Nobody has clocked in yet today.
            </div>
          )}
          {team?.present?.length > 0 && (
            <ul className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
              {team.present.map(p => (
                <li key={p.user_id} data-testid={`myday-team-present-${p.user_id}`} className="flex items-center gap-2 text-xs">
                  <UserCheck size={12} className="text-emerald-600 shrink-0"/>
                  <span className="font-semibold text-[color:var(--aii-text-1)] truncate flex-1">{p.name}</span>
                  <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded shrink-0 ${roleTone[p.role] || "bg-slate-300 text-slate-900"}`}>{p.role}</span>
                  <span className="text-[10px] text-[color:var(--aii-muted)] tabular-nums font-mono whitespace-nowrap shrink-0">
                    {p.check_in || "—"}{p.check_out ? ` → ${p.check_out}` : ""}
                  </span>
                  <HoursChip hours={p.hours}/>
                </li>
              ))}
            </ul>
          )}
          {team?.on_leave?.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mt-3 mb-1 flex items-center gap-1"><UserX size={9}/> On leave</div>
              <ul className="space-y-1 text-xs text-[color:var(--aii-muted)]">
                {team.on_leave.map(p => (
                  <li key={p.user_id} data-testid={`myday-team-leave-${p.user_id}`} className="flex items-center gap-2">
                    <span className="truncate">{p.name}</span>
                    {p.leave_reason && <span className="italic text-[10px] truncate">· {p.leave_reason}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {canParticipant && (
        <div className="aii-card p-4" data-testid="myday-participant-attendance">
          <TileHeader Icon={GraduationCap} label="Participants present · today" count={pax?.present_count ?? "—"} total={(pax?.present_count ?? 0) + (pax?.leave_count ?? 0)}/>
          <div className="text-[10px] text-[color:var(--aii-muted)] uppercase tracking-widest flex items-center gap-3 mb-2">
            <span className="flex items-center gap-1"><Clock size={10}/> <b className="text-brand text-xs normal-case tracking-normal">{pax?.total_hours ?? 0}h</b> logged</span>
            {pax?.leave_count > 0 && <span className="flex items-center gap-1"><UserX size={10}/> {pax.leave_count} leave</span>}
          </div>
          {pax === null && (
            <div className="text-xs text-[color:var(--aii-muted)] py-4 text-center">Loading…</div>
          )}
          {pax && pax.present.length === 0 && pax.on_leave.length === 0 && (
            <div className="text-xs text-[color:var(--aii-muted)] py-4 text-center italic">
              No participant has self-marked attendance yet today.
            </div>
          )}
          {pax?.present?.length > 0 && (
            <ul className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
              {pax.present.map(p => (
                <li key={p.participant_user_id} data-testid={`myday-pax-present-${p.participant_user_id}`} className="flex items-center gap-2 text-xs">
                  <UserCheck size={12} className="text-emerald-600 shrink-0"/>
                  <span className="font-semibold text-[color:var(--aii-text-1)] truncate flex-1">{p.name}</span>
                  <HoursChip hours={p.hours}/>
                </li>
              ))}
            </ul>
          )}
          {pax?.on_leave?.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mt-3 mb-1 flex items-center gap-1"><UserX size={9}/> On leave</div>
              <ul className="space-y-1 text-xs text-[color:var(--aii-muted)]">
                {pax.on_leave.map(p => (
                  <li key={p.participant_user_id} data-testid={`myday-pax-leave-${p.participant_user_id}`} className="flex items-center gap-2">
                    <span className="truncate">{p.name}</span>
                    {p.leave_reason && <span className="italic text-[10px] truncate">· {p.leave_reason}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
